import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/tyreMasterReport";

/* helpers */
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (iso) => {
  if (!iso) return "";
  // supports ISO OR dd/mm/yyyy already
  if (typeof iso === "string" && iso.includes("/")) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

// ✅ Normalize server row -> EXACT columns in your screenshot
function normalizeTyreRow(r) {
  return {
    vehicleMainType: pick(r, [
      "VehicleMainType",
      "vehicleMainType",
      "VehicleType",
      "fld_vehicletypename",
      "fld_vehiclemaintypename",
    ]),
    vehicleCode: pick(r, ["VehicleCode", "vehicleCode", "fld_vehicle_code", "fld_vehiclecode"]),
    vehicleName: pick(r, ["VehicleName", "vehicleName", "fld_vehicle_name", "fld_vehiclename"]),
    vehicleRegno: pick(r, ["VehicleRegno", "vehicleRegno", "vehicleRegNo", "regNo", "fld_vehicle_regno"]),

    tyreSize: pick(r, ["TyreSize", "tyreSize", "fld_tyresize"]),
    tyreSerialNo: pick(r, ["TyreSerialNo", "tyreSerialNo", "tyreSerial", "fld_tyreserialno"]),
    tyreModel: pick(r, ["TyreModel", "tyreModel", "fld_tyremodel"]),
    tyreMake: pick(r, ["TyreMake", "tyreMake", "fld_tyremake"]),

    issuedBranch: pick(r, ["IssuedBranch", "issuedBranch", "fld_issuebranch", "fld_issuedbranch", "fld_branchname"]),
    currentBranch: pick(r, ["fld_branchname", "currentBranch", "fld_currentbranch", "fld_carriedbranch"]),

    issuedDate: fmtDate(pick(r, ["IssuedDate", "issuedDate", "issueDate", "fld_issueddate", "fld_issuedate"])),
    supplier: pick(r, ["Supplier", "supplier", "fld_supplier", "fld_purchasefrom", "fld_Retrededfrom"]),
  };
}

export default function TyreMasterReportPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ tyreId from state OR query (?tyreId=123&tyreType=new)
  const tyreIdFromState = location?.state?.tyreId;
  const tyreIdFromQuery = searchParams.get("tyreId");
  const tyreId = useMemo(
    () => Number(tyreIdFromState || tyreIdFromQuery || 0),
    [tyreIdFromState, tyreIdFromQuery]
  );

  const tyreTypeFromState = location?.state?.tyreType;
  const tyreTypeFromQuery = searchParams.get("tyreType");
  const tyreType = useMemo(
    () => (tyreTypeFromState || tyreTypeFromQuery || "new"),
    [tyreTypeFromState, tyreTypeFromQuery]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        if (!tyreId) {
          setErr("Tyre ID missing");
          setLoading(false);
          return;
        }

        setLoading(true);
        setErr("");

        // ✅ backend should return: { success:true, row:{...} }
        // recommended: /print/:id?tyreType=new|retread
        const res = await axios.get(`${API_BASE}/print/${tyreId}`, {
          params: { tyreType },
        });

        const r = res?.data?.row;
        if (!r) throw new Error("Row not found in response");

        if (alive) setRow(r);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [tyreId, tyreType]);

  const x = useMemo(() => (row ? normalizeTyreRow(row) : null), [row]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}
          {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Tyre Master Report</h2>
        </div>

        {/* ✅ Table EXACTLY like your screenshot */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">VehicleMain Type</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Regno</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Size</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Serial No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Model</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Make</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Issued Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Current Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Issued Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Supplier</th>
              </tr>
            </thead>

            <tbody>
              {!x ? (
                <tr>
                  <td colSpan={13} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                <tr className="bg-white">
                  <td className="border border-black px-1 py-1 text-center">1</td>
                  <td className="border border-black px-1 py-1">{x.vehicleMainType}</td>
                  <td className="border border-black px-1 py-1 text-center">{x.vehicleCode}</td>
                  <td className="border border-black px-1 py-1">{x.vehicleName}</td>
                  <td className="border border-black px-1 py-1">{x.vehicleRegno}</td>
                  <td className="border border-black px-1 py-1">{x.tyreSize}</td>
                  <td className="border border-black px-1 py-1">{x.tyreSerialNo}</td>
                  <td className="border border-black px-1 py-1">{x.tyreModel}</td>
                  <td className="border border-black px-1 py-1">{x.tyreMake}</td>
                  <td className="border border-black px-1 py-1">{x.issuedBranch}</td>
                  <td className="border border-black px-1 py-1">{x.currentBranch}</td>
                  <td className="border border-black px-1 py-1 text-center">{x.issuedDate}</td>
                  <td className="border border-black px-1 py-1">{x.supplier}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
